/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { DeployEnv } from '../../../../ts';
import type { Account } from '../../../../ts/account';
import type { Assistant, Environment } from '../../../../ts/assistant';
import type { ChannelInstance } from '../../../../ts/channel';
import type { PlanMetering, PlanName } from '../../../../ts/plan';
import type { Skill } from '../../../../ts/skill';
import {
  getAccessControlProps,
  getAccountProps,
  getActionProps,
  getAssistantProps,
  getCustomExtensionProps,
  getEnvironmentProps,
  getTargetEnvironmentProps,
  getIdentifyProps,
  getInstanceProps,
  getIntegrationProps,
  getNewIaProps,
  getProductProps,
  getSkillProps,
  getStepProps
} from './segmentProperties';

const eventTypes = {
  trackEvent: 'trackEvent',
  pageEvent: 'pageEvent'
};

class SegmentEvent {
  type: string;
  name: string;
  eventData: any;
  commonData: {
    productId: string;
    productTitle: string;
    environment: DeployEnv;
  };
  identifyData: any;

  constructor(type, name, data) {
    this.type = type;
    this.name = name;
    this.eventData = data;
    this.commonData = getProductProps();
  }

  get data() {
    return { ...this.commonData, ...this.eventData };
  }

  addData(data) {
    this.commonData = { ...this.commonData, ...data };
  }
  addNewIaFlag(isNewIa: boolean) {
    this.addData(getNewIaProps(isNewIa));
  }

  addAccountData(account: Account) {
    this.addData(getAccountProps(account));
  }

  addInstanceData(
    instance: string,
    region: string,
    plan: PlanName,
    metering: PlanMetering | undefined
  ) {
    this.addData(getInstanceProps(instance, region, plan, metering));
  }

  addAccessControlData(roles) {
    this.addData(getAccessControlProps(roles));
  }

  addAssistantData(assistant: Assistant, roles) {
    this.addData(getAssistantProps(assistant, roles));
  }

  addSkillData(skill: Skill, roles) {
    this.addData(getSkillProps(skill, roles));
  }

  addStepData(actionsStepData) {
    this.addData(getStepProps(actionsStepData));
  }

  addEnvironmentData(environmentData: Environment) {
    this.addData(getEnvironmentProps(environmentData));
  }

  addTargetEnvironmentData(environmentData: Environment) {
    this.addData(getTargetEnvironmentProps(environmentData));
  }

  addCustomExtensionData(customExtensionData) {
    this.addData(getCustomExtensionProps(customExtensionData));
  }

  addIntegrationData(integrationData: ChannelInstance) {
    this.addData(getIntegrationProps(integrationData));
  }

  addActionData(
    actionsCurrentAction,
    actionDetails,
    actionsTriggerExamples,
    actionsAutoSaveEnabled,
    actionsAllSteps,
    assistant,
    confirmation_enabled
  ) {
    this.addData(
      getActionProps(
        actionsCurrentAction,
        actionDetails,
        actionsTriggerExamples,
        actionsAutoSaveEnabled,
        actionsAllSteps,
        assistant,
        confirmation_enabled
      )
    );
  }

  addIdentifyData(plan) {
    this.identifyData = getIdentifyProps(plan);
  }
}

export { eventTypes };
export default SegmentEvent;
